import { useEffect, useMemo, useState, useCallback, useRef } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import type { PostCard } from '../types'
import { resolveLandscapeCover } from '../lib/cover'
import NavBar from '../components/layout/NavBar'
import ActionRail from '../components/ActionRail'
import MagazineHtml from '../components/renderers/MagazineHtml'
import { linkifyMagazineImageNodes } from '../components/renderers/magazineRenderConfig'
import { useGiftFlow } from '../hooks/useGiftFlow'
import GiftDrawer from '../components/gifts/GiftDrawer'
import GiftToast from '../components/gifts/GiftToast'
import GiftCelebration from '../components/gifts/GiftCelebration'
import CommentsDrawer from '../components/comments/CommentsDrawer'
import { ReaderSettingsProvider, useReaderSettings } from '../components/reader/ReaderSettings'
import ChapterPill from '../components/reader/ChapterPill'
import TOCSettingsDrawer from '../components/reader/TOCSettingsDrawer'
import { buildTOCFromHtml, type TOCItem } from '../lib/reader/toc'
import ReviewsInline from '../components/comments/ReviewsInline'
import ReportModal from '../components/moderation/ReportModal'
import { api } from '../lib/api'
import { resolveMediaUrl } from '../lib/mediaUrl'
import SubscribeModal from '../components/subscriptions/SubscribeModal'
import UnlockModal from '../components/subscriptions/UnlockModal'
import MicroMetaFooter from '../components/micro/MicroMetaFooter'
import FormatWatermark from '../components/common/FormatWatermark'
import { changeSubscriptionTier, subscribeToCreator } from '../api/subscriptions'
import { unlockPost } from '../api/unlocks'
import { newIdempotencyKey } from '../lib/idempotency'
import { getCreatorSubscriptionSettingsPublic, type CreatorSubscriptionSettingsPublic } from '../api/creatorSubscriptions'
import { paidBadgeLabel, type AccessLevel } from '../features/editor/access'
import { getCreatorEmailPreference, patchCreatorEmailPreference } from '../api/subscriptionEmail'
import { getPostReceipt, purchaseEventTicket, type PostReceipt } from '../api/posts'
import { getWalletTickets, type WalletTicketSummary } from '../api/wallet'
import TicketQrModal from '../components/wallet/TicketQrModal'
import SensitiveMedia from '../components/content/SensitiveMedia'

type PostDetail = PostCard & { body?: string; created_at?: string }

export default function Reader() {
  const navigate = useNavigate()
  const location = useLocation()
  const { idSlug } = useParams()
  const id = useMemo(() => {
    if (!idSlug) return null
    const num = parseInt(String(idSlug).split('-')[0], 10)
    return Number.isFinite(num) ? num : null
  }, [idSlug])

  const [post, setPost] = useState<PostDetail | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!id) return
    let cancelled = false
    async function load() {
      try {
        const query = new URLSearchParams(location.search || '')
        const forceCanonical = query.get('force_canonical_html') === '1'
        const { data } = await api.get<PostDetail>(`/api/posts/${id}/`, {
          params: forceCanonical ? { force_canonical_html: '1' } : undefined,
        })
        if (!cancelled) setPost(data)
      } catch (e: any) {
        const msg =
          e?.response?.data?.detail ||
          e?.response?.data?.message ||
          e?.message ||
          'Failed to load'
        if (!cancelled) setError(msg)
      }
    }
    load()
    return () => { cancelled = true }
  }, [id, location.search])

  const cover = resolveLandscapeCover(post)

  const [commentsOpen, setCommentsOpen] = useState(false)
  const openComments = useCallback(() => setCommentsOpen(true), [])
  const closeComments = useCallback(() => setCommentsOpen(false), [])
  const [reportOpen, setReportOpen] = useState(false)
  const openReport = useCallback(() => setReportOpen(true), [])
  const closeReport = useCallback(() => setReportOpen(false), [])
  const tapRef = useRef<{ x: number; y: number; moved: boolean; t: number } | null>(null)

  const isMobile = () => (typeof window !== 'undefined' ? window.matchMedia('(max-width: 767px)').matches : false)
  const tapBack = useCallback(() => {
    if (window.history.length > 1) navigate(-1)
    else navigate('/')
  }, [navigate])

  // Gifts wiring: mirror mobile behavior minimally
  const giftFlow = useGiftFlow({
    updateTips: (pid, tips) => {
      setPost((prev) => (prev && prev.id === pid ? { ...prev, counts: { ...prev.counts, tips } } : prev))
    },
  })

  return (
    <div
      className="min-h-screen"
      onPointerDown={(e) => {
        if (!isMobile()) return
        const t = e.target as HTMLElement
        if (t?.closest?.('[data-no-tapback="1"]')) return
        if (t?.closest?.('a,button,input,textarea,select,label,[role="button"],.copy-code-btn')) return
        tapRef.current = { x: e.clientX, y: e.clientY, moved: false, t: Date.now() }
      }}
      onPointerMove={(e) => {
        const s = tapRef.current
        if (!s) return
        const dx = Math.abs(e.clientX - s.x)
        const dy = Math.abs(e.clientY - s.y)
        if (dx > 12 || dy > 12) s.moved = true
      }}
      onPointerUp={(e) => {
        if (!isMobile()) return
        const s = tapRef.current
        tapRef.current = null
        if (!s) return
        const dt = Date.now() - s.t
        if (s.moved || dt > 650) return
        const t = e.target as HTMLElement
        if (t?.closest?.('[data-no-tapback="1"]')) return
        if (t?.closest?.('a,button,input,textarea,select,label,[role="button"],.copy-code-btn')) return
        const sel = window.getSelection?.()
        if (sel && sel.toString().trim()) return
        tapBack()
      }}
    >
      <NavBar showSearch={false} showMobileModePill={false} />

      <div className="mx-auto max-w-6xl px-4 pt-4">
        <button
          type="button"
          onClick={() => {
            if (window.history.length > 1) navigate(-1)
            else navigate('/publish')
          }}
          className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 py-1.5 text-sm text-white/85 hover:bg-white/10"
        >
          <svg viewBox="0 0 24 24" className="h-4 w-4 fill-white/85" aria-hidden="true">
            <path d="M15.41 7.41 14 6l-6 6 6 6 1.41-1.41L10.83 12z" />
          </svg>
          Back to Publish
        </button>
      </div>

      {error && <div className="mx-auto max-w-4xl px-4 py-6 text-red-400">{error}</div>}

      {!post && !error && <div className="mx-auto max-w-4xl px-4 py-6 glass rounded-xl h-64" />}

      {post && (
        <ReaderSettingsProvider>
        <PaywallGate post={post} onPostUpdate={setPost} />
        <ReaderInner
          post={post}
          cover={cover}
          openComments={openComments}
          giftOpen={() => giftFlow.openForPost(post as any)}
          onReport={openReport}
          onPostUpdate={(next) => setPost((prev) => (prev ? { ...prev, ...next } : prev))}
        />
        </ReaderSettingsProvider>
      )}

      {/* Gift Drawer + Toast */}
      {post && (
        <>
          <GiftDrawer
            open={giftFlow.isOpen}
            onClose={giftFlow.close}
            creatorName={post?.user?.displayName}
            avatarUrl={resolveMediaUrl(post?.user?.avatar) || post?.user?.avatar}
            catalog={giftFlow.catalogDefs}
            onSend={({ gift }) => giftFlow.onSend({ gift })}
          />
          <GiftToast
            visible={giftFlow.toast.visible}
            emoji={giftFlow.toast.emoji}
            label={giftFlow.toast.label}
            detail={giftFlow.toast.detail}
            kind={giftFlow.toast.kind}
            onHide={giftFlow.toast.hide}
          />
          <GiftCelebration visible={giftFlow.toast.visible && giftFlow.toast.kind === 'success' && giftFlow.toast.celebrate} />
        </>
      )}

      {/* Comments Drawer */}
      {post && (
        <CommentsDrawer open={commentsOpen} onClose={closeComments} target={{ type: 'post', id: post.id }} />
      )}
      {post && (
        <ReportModal
          open={reportOpen}
          onClose={closeReport}
          contentType="post"
          contentId={String(post.id)}
          title={post.title ? `Report "${post.title}"` : 'Report post'}
        />
      )}
    </div>
  )
}

function PaywallGate({ post, onPostUpdate }: { post: PostDetail; onPostUpdate: (p: PostDetail) => void }) {
  const locked = post.is_unlocked === false && post.access_level && post.access_level !== 'FREE'
  const [subOpen, setSubOpen] = useState(false)
  const [unlockOpen, setUnlockOpen] = useState(false)
  const [busy, setBusy] = useState<'sub' | 'unlock' | null>(null)
  const [err, setErr] = useState<string | null>(null)
  const [creatorPublic, setCreatorPublic] = useState<CreatorSubscriptionSettingsPublic | null>(null)
  const [receivePostsEmail, setReceivePostsEmail] = useState(false)

  if (!locked) return null

  const creatorId = post.user_id ?? post.user?.id
  const price = post.cta?.ppv_price_pixe ?? null

  const canSub = Boolean(post.cta?.can_subscribe) && Boolean(creatorId)
  const canUpgrade = Boolean(post.cta?.can_upgrade) && Boolean(creatorId)
  const canUnlock = Boolean(post.cta?.can_ppv_unlock) && Boolean(price)

  useEffect(() => {
    if (!subOpen) return
    if (!creatorId) return
    let cancelled = false
    ;(async () => {
      try {
        const s = await getCreatorSubscriptionSettingsPublic(creatorId)
        if (!cancelled) setCreatorPublic(s)
      } catch {}
    })()
    return () => { cancelled = true }
  }, [subOpen, creatorId])

  useEffect(() => {
    if (!subOpen || !creatorId) return
    getCreatorEmailPreference(creatorId)
      .then((pref) => setReceivePostsEmail(Boolean(pref.receive_posts)))
      .catch(() => {})
  }, [subOpen, creatorId])

  async function doSubscribeOrUpgrade(tierId?: number | null, opts?: { receivePostsEmail?: boolean }) {
    if (!creatorId) return
    setErr(null)
    setBusy('sub')
    try {
      if (canUpgrade) {
        if (!tierId) throw new Error('Choose a tier to upgrade.')
        await changeSubscriptionTier(creatorId, { tier_id: tierId, change_type: 'upgrade' })
      } else {
        await subscribeToCreator(creatorId, { idempotencyKey: newIdempotencyKey('sub'), tierId: tierId ?? null })
      }
      await patchCreatorEmailPreference({
        creator_id: creatorId,
        receive_posts: Boolean(opts?.receivePostsEmail),
      })
      const { data } = await api.get<PostDetail>(`/api/posts/${post.id}/`)
      onPostUpdate(data)
      setSubOpen(false)
    } catch (e: any) {
      setErr(e?.response?.data?.detail || e?.response?.data?.message || e?.message || 'Subscribe failed')
    } finally {
      setBusy(null)
    }
  }

  async function doUnlock() {
    setErr(null)
    setBusy('unlock')
    try {
      const res = await unlockPost(post.id, { idempotencyKey: newIdempotencyKey('unlock') })
      if (res?.post) onPostUpdate(res.post)
      setUnlockOpen(false)
    } catch (e: any) {
      setErr(e?.response?.data?.detail || e?.response?.data?.message || e?.message || 'Unlock failed')
    } finally {
      setBusy(null)
    }
  }

  return (
    <>
      <div className="mx-auto max-w-4xl px-4 pt-6">
        <div className="rounded-2xl border border-white/10 bg-white/5 p-5">
          <div className="text-sm text-white/70">Preview</div>
          <div className="mt-2 text-white/85 leading-relaxed">{post.preview_excerpt || post.body_plain || ''}</div>
          <div className="mt-4 flex flex-wrap gap-2">
            {(canSub || canUpgrade) && (
              <button
                type="button"
                onClick={() => { setErr(null); setSubOpen(true) }}
                className="rounded-xl bg-brand px-4 py-2 text-sm font-semibold text-white hover:bg-brand-600"
              >
                {canUpgrade ? 'Upgrade tier' : 'Subscribe'}
              </button>
            )}
            {canUnlock && (
              <button
                type="button"
                onClick={() => { setErr(null); setUnlockOpen(true) }}
                className="rounded-xl border border-white/10 bg-white/5 px-4 py-2 text-sm font-semibold hover:bg-white/10"
              >
                Unlock for {price} PIXE
              </button>
            )}
            {!canSub && !canUnlock && (
              <div className="text-sm text-white/70">This Pixe is locked.</div>
            )}
          </div>
          {err ? <div className="mt-3 text-sm text-red-400">{err}</div> : null}
        </div>
      </div>

      <SubscribeModal
        open={subOpen}
        onClose={() => setSubOpen(false)}
        creatorName={post.user?.displayName}
        monthlyPricePixe={creatorPublic?.monthly_price_pixe ?? null}
        tiers={(creatorPublic as any)?.tiers ?? null}
        features={creatorPublic?.features ?? null}
        intent={canUpgrade ? 'upgrade' : 'subscribe'}
        receivePostsEmail={receivePostsEmail}
        onChangeReceivePostsEmail={setReceivePostsEmail}
        onConfirm={doSubscribeOrUpgrade}
        busy={busy === 'sub'}
        error={err}
      />
      <UnlockModal
        open={unlockOpen}
        onClose={() => setUnlockOpen(false)}
        pricePixe={price}
        onConfirm={doUnlock}
        busy={busy === 'unlock'}
        error={err}
      />
    </>
  )
}

function ReaderInner({ post, cover, openComments, giftOpen, onReport, onPostUpdate }: { post: PostCard & { body?: string; body_html?: string }; cover?: string | null; openComments: () => void; giftOpen: () => void; onReport: () => void; onPostUpdate?: (next: Partial<PostCard>) => void }) {
  const navigate = useNavigate()
  const { settings } = useReaderSettings()
  const containerRef = useRef<HTMLDivElement | null>(null)
  const [processedHtml, setProcessedHtml] = useState<string>('')
  const [toc, setToc] = useState<TOCItem[]>([])
  const [activeId, setActiveId] = useState<string | null>(null)
  const [tocOpen, setTocOpen] = useState(false)
  const [progress, setProgress] = useState(0)
  const [resumePct, setResumePct] = useState<number | null>(null)

  useEffect(() => {
    const rawHtml = linkifyMagazineImageNodes((post as any)?.body_html || '')
    if (rawHtml && rawHtml.trim()) {
      const { html, toc } = buildTOCFromHtml(rawHtml)
      setProcessedHtml(html)
      setToc(toc)
    } else {
      const safe = (post.body || '').split('\n').map((l) => l.trim()).filter(Boolean).map((p) => `<p>${p}</p>`).join('')
      const { html, toc } = buildTOCFromHtml(safe)
      setProcessedHtml(html)
      setToc(toc)
    }
  }, [post])

  // Scroll spy for headings
  useEffect(() => {
    const root = containerRef.current
    if (!root || toc.length === 0) return
    const headings = toc.map((t) => root.querySelector(`#${CSS.escape(t.id)}`)).filter(Boolean) as Element[]
    const obs = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => (a.boundingClientRect.top - b.boundingClientRect.top))
        if (visible[0]) setActiveId((visible[0].target as HTMLElement).id)
      },
      { rootMargin: '-20% 0px -60% 0px', threshold: [0, 1] }
    )
    headings.forEach((el) => obs.observe(el))
    return () => obs.disconnect()
  }, [toc])

  const jumpTo = useCallback((id: string) => {
    setTocOpen(false)
    const root = containerRef.current
    const el = root?.querySelector(`#${CSS.escape(id)}`) as HTMLElement | null
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }, [])

  // Prism highlight + copy buttons for code blocks
  useEffect(() => {
    const root = containerRef.current
    if (!root) return
    try { (window as any).Prism?.highlightAllUnder?.(root) } catch {}
    // Add copy buttons to code blocks
    const pres = Array.from(root.querySelectorAll('pre')) as HTMLElement[]
    pres.forEach((pre) => {
      if (pre.querySelector('.copy-code-btn')) return
      const btn = document.createElement('button')
      btn.className = 'copy-code-btn absolute right-2 top-2 text-xs px-2 py-1 rounded bg-white/10 hover:bg-white/20 border border-white/10'
      btn.textContent = 'Copy'
      btn.addEventListener('click', async (e) => {
        e.stopPropagation()
        const code = pre.querySelector('code')
        const text = code?.textContent || ''
        try { await navigator.clipboard.writeText(text); btn.textContent = 'Copied!'; setTimeout(() => (btn.textContent = 'Copy'), 1200) } catch {}
      })
      pre.style.position = 'relative'
      pre.appendChild(btn)
    })
  }, [processedHtml])

  // Progress tracking + resume
  useEffect(() => {
    const key = `reader:post:${post.id}:progress`
    const onScroll = () => {
      const root = containerRef.current
      if (!root) return
      const rect = root.getBoundingClientRect()
      const viewportH = window.innerHeight || document.documentElement.clientHeight
      const contentTop = window.scrollY + rect.top
      const contentHeight = root.scrollHeight
      const y = window.scrollY
      const pct = Math.min(1, Math.max(0, (y - contentTop) / Math.max(1, contentHeight - viewportH)))
      setProgress(pct)
      try { localStorage.setItem(key, String(pct)) } catch {}
    }
    window.addEventListener('scroll', onScroll, { passive: true })
    onScroll()
    // seed resume value once
    try {
      const saved = localStorage.getItem(`reader:post:${post.id}:progress`)
      if (saved) setResumePct(Number(saved))
    } catch {}
    return () => window.removeEventListener('scroll', onScroll)
  }, [post.id])

  const handleResume = useCallback(() => {
    const pct = resumePct ?? 0
    const root = containerRef.current
    if (!root) return
    const viewportH = window.innerHeight || document.documentElement.clientHeight
    const contentTop = window.scrollY + root.getBoundingClientRect().top
    const target = contentTop + (root.scrollHeight - viewportH) * pct
    window.scrollTo({ top: target, behavior: 'smooth' })
  }, [resumePct])

  // Update URL hash with active heading
  useEffect(() => {
    if (!activeId) return
    const url = new URL(window.location.href)
    if (url.hash !== `#${activeId}`) {
      url.hash = activeId
      window.history.replaceState({}, '', url.toString())
    }
  }, [activeId])

  // On mount, if hash present, scroll to it; else consider resume
  useEffect(() => {
    const hash = window.location.hash.replace(/^#/, '')
    if (hash) {
      setTimeout(() => jumpTo(hash), 50)
    }
  }, [jumpTo])

  const themeBg = settings.theme === 'light' ? '#ffffff' : settings.theme === 'sepia' ? '#f4ecd8' : '#0a0a0a'
  const themeFg = settings.theme === 'light' ? '#111111' : settings.theme === 'sepia' ? '#2a1f0f' : '#ffffff'
  const accessLevel = (post.access_level || 'FREE') as AccessLevel
  const badge = paidBadgeLabel(accessLevel, (post as any)?.required_tier_rank ?? null)
  const isMicro = post.format === 'micro'
  const microPrimaryText = String((post as any)?.micro_text || post.title || '').trim()
  const hasExpandedContent = Boolean(String((post as any)?.body_html || '').trim() || String(post.body || '').trim())
  const hasProof = Boolean((post as any)?.has_proof || (post as any)?.receipt_id || (post as any)?.chain_receipt_id)
  const authorAvatar = resolveMediaUrl(post.user?.avatar) || post.user?.avatar || ''
  const [receiptDetails, setReceiptDetails] = useState<PostReceipt | null>(null)
  const [ticketBusy, setTicketBusy] = useState(false)
  const [ticketError, setTicketError] = useState<string | null>(null)
  const [ticketChargeSummary, setTicketChargeSummary] = useState<{ total: number; fee: number; net: number } | null>(null)
  const [ticketEmailQueued, setTicketEmailQueued] = useState(false)
  const [ticketSummary, setTicketSummary] = useState<WalletTicketSummary | null>(null)
  const [ticketModalOpen, setTicketModalOpen] = useState(false)
  const [ticketReceiptRefreshing, setTicketReceiptRefreshing] = useState(false)

  const loadTicketSummary = useCallback(async () => {
    const page = await getWalletTickets()
    const found = (page.results || []).find((r) => Number(r.post_id) === Number(post.id)) || null
    setTicketSummary(found)
  }, [post.id])

  useEffect(() => {
    if (!(post.format === 'event' && post.viewer_has_ticket)) {
      setTicketSummary(null)
      return
    }
    ;(async () => {
      try {
        await loadTicketSummary()
      } catch {
        setTicketSummary(null)
      }
    })()
  }, [post.id, post.format, post.viewer_has_ticket, loadTicketSummary])

  useEffect(() => {
    if (String(ticketSummary?.receipt_status || '') !== 'pending') return
    const t = window.setInterval(() => {
      void loadTicketSummary()
    }, 10_000)
    return () => window.clearInterval(t)
  }, [ticketSummary?.receipt_status, loadTicketSummary])

  useEffect(() => {
    let cancelled = false
    if (!isMicro) {
      setReceiptDetails(null)
      return
    }
    if (!hasProof && !(post as any)?.receipt_id && !(post as any)?.chain_receipt_id) {
      setReceiptDetails(null)
      return
    }
    ;(async () => {
      try {
        const details = await getPostReceipt(post.id)
        if (!cancelled) setReceiptDetails(details)
      } catch {
        if (!cancelled) setReceiptDetails(null)
      }
    })()
    return () => { cancelled = true }
  }, [isMicro, hasProof, post.id, (post as any)?.receipt_id, (post as any)?.chain_receipt_id])

  return (
    <main className="mx-auto max-w-3xl px-4 py-6 relative">
      {/* Top progress bar */}
      <div className="fixed left-0 right-0 top-0 z-40 h-0.5 bg-white/10">
        <div className="h-full bg-brand" style={{ width: `${Math.round(progress * 100)}%` }} />
      </div>
      {!isMicro && cover && (
        <div className="rounded-xl overflow-hidden mb-4">
          <SensitiveMedia imageUrl={cover} warning={(post as any)?.content_warning || null} alt={post.title} className="w-full h-auto" />
        </div>
      )}
      <FormatWatermark
        format={post.format}
        category={(post as any)?.category || (post as any)?.category_slug || null}
        className="mb-2"
      />
      <div className="flex flex-wrap items-start justify-between gap-3" data-no-tapback="1">
        <h1 className={isMicro ? 'text-3xl md:text-4xl font-extrabold tracking-tight whitespace-pre-line leading-tight' : 'text-3xl font-extrabold tracking-tight'}>
          {isMicro ? (microPrimaryText || post.title) : post.title}
        </h1>
        {badge ? (
          <span
            className="inline-flex items-center rounded-full border border-white/10 bg-black/50 px-2.5 py-1 text-[11px] font-semibold text-white/90"
            title="Access level"
          >
            {badge}
          </span>
        ) : null}
      </div>
      {String((post as any)?.moderation_state || '').toLowerCase() === 'pending_review' ? (
        <div className="mt-2 inline-flex rounded-full border border-amber-300/40 bg-amber-300/10 px-2.5 py-1 text-xs font-semibold text-amber-200">
          Under Review
        </div>
      ) : null}
      {post.format === 'event' && post.event_is_ticketed ? (
        <div className="mt-3 rounded-xl border border-white/10 bg-white/5 p-3 flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="text-sm font-semibold text-white">{post.event_ticket_price_pixe || 0} PIXE per ticket</div>
            <div className="text-xs text-white/70">
              {post.tickets_remaining != null ? `${post.tickets_remaining} remaining` : 'Capacity not set'}
              {post.viewer_ticket_count ? ` • You own ${post.viewer_ticket_count}` : ''}
            </div>
          </div>
          {post.viewer_has_ticket ? (
            <div className="flex items-center gap-2">
              <button type="button" disabled className="rounded-xl border border-emerald-400/40 bg-emerald-500/20 px-4 py-2 text-sm font-semibold text-emerald-100">
                You're going
              </button>
              <button
                type="button"
                data-testid="view-ticket-button"
                onClick={() => setTicketModalOpen(true)}
                className="rounded-xl border border-white/15 bg-white/5 px-4 py-2 text-sm font-semibold text-white hover:bg-white/10"
              >
                View ticket
              </button>
            </div>
          ) : (
            <button
              type="button"
              data-testid="buy-ticket-button"
              disabled={ticketBusy || (post.tickets_remaining != null && post.tickets_remaining <= 0)}
              onClick={async () => {
                setTicketError(null)
                setTicketBusy(true)
                try {
                  const res = await purchaseEventTicket(post.id, 1)
                setTicketChargeSummary({
                  total: Number(res.total_pixe || 0),
                  fee: Number(res.fee_pixe || 0),
                  net: Number(res.net_pixe || 0),
                })
                setTicketEmailQueued(Boolean(res.ticket_email_queued))
                setTicketSummary((prev) => ({
                  post_id: Number(post.id),
                  event_title: String(post.title || ''),
                  starts_at: post.event_starts_at || null,
                  ends_at: post.event_ends_at || null,
                  city_label: String(post.local_city || ''),
                  venue_name: String(post.event_venue_name || ''),
                  address: String(post.event_address || ''),
                  qty: Number(res.viewer_ticket_count || 1),
                  status: 'active',
                  last_order_id: Number(res.order?.id || 0),
                  purchased_at: new Date().toISOString(),
                  cover_image_url: post.cover_image_url || '',
                  receipt_hash: String(res.receipt_hash || ''),
                  receipt_status: String(res.receipt_status || 'pending'),
                  receipt_tx_hash: String(res.poe_tx_hash || ''),
                  receipt_block_number: (res.poe_block_number as any) ?? null,
                  ticket_serials: res.tickets?.serials || prev?.ticket_serials || [],
                  ticket_qr_payloads: res.ticket_qr_payloads || prev?.ticket_qr_payloads || [],
                }))
                onPostUpdate?.({
                  viewer_has_ticket: Boolean(res.viewer_has_ticket),
                  viewer_ticket_count: Number(res.viewer_ticket_count || 0),
                  tickets_sold: Number(res.tickets_sold || 0),
                  tickets_remaining: Number(res.tickets_remaining || 0),
                  viewer_ticket_receipt_hash: String(res.receipt_hash || ''),
                  viewer_ticket_receipt_status: String(res.receipt_status || ''),
                  viewer_ticket_receipt_tx_hash: String(res.poe_tx_hash || ''),
                } as Partial<PostCard>)
              } catch (e: any) {
                  const status = Number(e?.response?.status || 0)
                  if (status === 401) {
                    navigate(`/login?next=${encodeURIComponent(window.location.pathname + window.location.search)}`)
                    return
                  }
                  setTicketError(e?.response?.data?.detail || e?.message || 'Ticket purchase failed')
                } finally {
                  setTicketBusy(false)
                }
              }}
              className="rounded-xl bg-brand px-4 py-2 text-sm font-semibold text-white hover:bg-brand-600 disabled:opacity-60"
            >
              {post.tickets_remaining != null && post.tickets_remaining <= 0 ? 'Sold out' : ticketBusy ? 'Buying...' : `Buy ticket • ${post.event_ticket_price_pixe || 0} PIXE`}
            </button>
          )}
          {ticketError ? <div className="w-full text-xs text-red-300">{ticketError}</div> : null}
          {!ticketError && ticketChargeSummary ? (
            <div className="w-full text-xs text-white/60">
              Paid {ticketChargeSummary.total} PIXE (fee {ticketChargeSummary.fee} PIXE, organizer {ticketChargeSummary.net} PIXE).
            </div>
          ) : null}
          {!ticketError && ticketChargeSummary && ticketEmailQueued ? (
            <div className="w-full text-xs text-emerald-200">
              Ticket receipt emailed to your account email.
            </div>
          ) : null}
          {!ticketError && (ticketSummary?.receipt_status || (post as any)?.viewer_ticket_receipt_status) ? (
            <div className={`w-full text-xs ${(ticketSummary?.receipt_status || (post as any)?.viewer_ticket_receipt_status) === 'anchored' ? 'text-emerald-300' : 'text-amber-200'}`}>
              {(ticketSummary?.receipt_status || (post as any)?.viewer_ticket_receipt_status) === 'anchored' ? 'Verified receipt anchored on PixeChain.' : 'Receipt anchoring pending.'}
            </div>
          ) : null}
        </div>
      ) : null}
      <TicketQrModal
        open={ticketModalOpen}
        onClose={() => setTicketModalOpen(false)}
        title={post.title || 'Ticket'}
        qrImageUrl={ticketSummary?.ticket_serials?.[0] ? `/api/tickets/${ticketSummary.ticket_serials[0]}/qr.png` : null}
        qrPayload={ticketSummary?.ticket_qr_payloads?.[0] || null}
        receiptHash={ticketSummary?.receipt_hash || (post as any)?.viewer_ticket_receipt_hash || null}
        receiptStatus={ticketSummary?.receipt_status || (post as any)?.viewer_ticket_receipt_status || null}
        onRefreshReceipt={async () => {
          setTicketReceiptRefreshing(true)
          try {
            await loadTicketSummary()
          } finally {
            setTicketReceiptRefreshing(false)
          }
        }}
        refreshingReceipt={ticketReceiptRefreshing}
        pendingMessage={
          String(ticketSummary?.receipt_status || (post as any)?.viewer_ticket_receipt_status || '') === 'pending'
            ? 'Receipt verification is pending. Tap refresh to check again.'
            : null
        }
      />
      {post.user && (
        <div className="mt-3 flex items-center gap-3 text-sm text-white/80" data-no-tapback="1">
          {authorAvatar ? <img src={authorAvatar} className="h-8 w-8 rounded-full" /> : <div className="h-8 w-8 rounded-full bg-white/10" />}
          <div>
            <div className="font-semibold">{post.user.displayName}</div>
            <div className="text-white/70">{post.user.handle}</div>
          </div>
        </div>
      )}

      {isMicro ? (
        <div className="mt-3" data-no-tapback="1">
          <MicroMetaFooter
            createdAt={(post as any)?.created_at || null}
            viewsCount={(post as any)?.views_count ?? 0}
            hasProof={hasProof}
            receiptStatus={(post as any)?.receipt_status || receiptDetails?.receipt_status || receiptDetails?.status || null}
            compact={false}
          />
        </div>
      ) : null}

      {isMicro && hasProof ? (
        <section className="mt-4 rounded-xl border border-white/10 bg-white/5 p-4" data-no-tapback="1">
          <div className="mb-2 text-xs uppercase tracking-[0.14em] text-white/60">Proof</div>
          <div className="grid gap-2 text-xs text-white/75 md:grid-cols-2">
            <div>
              <span className="text-white/55">Status:</span>{' '}
              <span className="font-semibold text-white/85">
                {String((post as any)?.receipt_status || receiptDetails?.receipt_status || receiptDetails?.status || 'confirmed')}
              </span>
            </div>
            <div>
              <span className="text-white/55">Receipt ID:</span>{' '}
              <span className="font-mono text-white/80 break-all">
                {String((post as any)?.receipt_id || (post as any)?.chain_receipt_id || receiptDetails?.receipt_id || '')}
              </span>
            </div>
            {(receiptDetails?.chain_tx_id || (post as any)?.chain_tx_id) ? (
              <div>
                <span className="text-white/55">Tx:</span>{' '}
                <span className="font-mono text-white/80 break-all">{String(receiptDetails?.chain_tx_id || (post as any)?.chain_tx_id)}</span>
              </div>
            ) : null}
            {(receiptDetails?.chain_block || (post as any)?.chain_block) ? (
              <div>
                <span className="text-white/55">Block:</span>{' '}
                <span className="font-mono text-white/80">{String(receiptDetails?.chain_block || (post as any)?.chain_block)}</span>
              </div>
            ) : null}
            {(receiptDetails?.content_hash || (post as any)?.content_hash) ? (
              <div className="md:col-span-2">
                <span className="text-white/55">Content hash:</span>{' '}
                <span className="font-mono text-white/80 break-all">{String(receiptDetails?.content_hash || (post as any)?.content_hash)}</span>
              </div>
            ) : null}
          </div>
          <div className="mt-3 flex flex-wrap gap-2 text-xs">
            {((post as any)?.receipt_download_url || receiptDetails?.receipt_download_url) ? (
              <a
                href={String((post as any)?.receipt_download_url || receiptDetails?.receipt_download_url)}
                className="rounded-full border border-white/15 bg-white/5 px-3 py-1.5 text-white/85 hover:bg-white/10"
              >
                Receipt PDF/JSON
              </a>
            ) : null}
            {(receiptDetails?.explorer_url) ? (
              <a
                href={String(receiptDetails.explorer_url)}
                target="_blank"
                rel="noreferrer"
                className="rounded-full border border-white/15 bg-white/5 px-3 py-1.5 text-white/85 hover:bg-white/10"
              >
                View on Chain
              </a>
            ) : null}
            {((post as any)?.receipt_qr_url ? (
              <a
                href={String((post as any)?.receipt_qr_url)}
                target="_blank"
                rel="noreferrer"
                className="rounded-full border border-white/15 bg-white/5 px-3 py-1.5 text-white/85 hover:bg-white/10"
              >
                QR
              </a>
            ) : null)}
          </div>
        </section>
      ) : null}

      {!isMicro && post.subtitle && <p className="mt-4 text-lg text-white/85">{post.subtitle}</p>}
      {/* Resume prompt */}
      {!window.location.hash && resumePct !== null && resumePct > 0.05 && resumePct < 0.98 && (
        <div className="mt-3 text-sm text-white/85">
          <button onClick={handleResume} className="underline">Resume where you left off ({Math.round((resumePct ?? 0) * 100)}%)</button>
        </div>
      )}

      {(hasExpandedContent || !isMicro) ? (
        <MagazineHtml
          ref={containerRef}
          surface="postPublished"
          html={processedHtml}
          style={{
            background: themeBg,
            color: themeFg,
            padding: '1px 0',
            fontFamily: settings.fontFamily === 'serif' ? 'Georgia, serif' : 'Inter, system-ui, sans-serif',
            fontSize: settings.fontSize,
            lineHeight: `${settings.lineHeight}px`,
          }}
        />
      ) : null}

      {/* Inline Comments (continuation of content) */}
      <ReviewsInline target={{ type: 'post', id: post.id }} />

      {/* Chapter pill (open TOC/Settings) */}
      <ChapterPill currentLabel={activeId ? toc.find((t) => t.id === activeId)?.text : undefined} onOpen={() => setTocOpen(true)} />

      {/* Action rail overlay */}
      {post.counts && (
          <ActionRail
            postId={post.id}
            counts={post.counts}
            myState={post.my_state}
            user={{ ...post.user, avatar: authorAvatar }}
            shareUrl={(post as any).share_url}
            onOpenComments={openComments}
            onGift={giftOpen}
            onReport={onReport}
          />
      )}

      {/* TOC + Settings Drawer */}
      <TOCSettingsDrawer open={tocOpen} onClose={() => setTocOpen(false)} toc={toc} activeId={activeId ?? undefined} onJumpTo={jumpTo} />

      {/* Author bio + related (basic) */}
      <section className="mt-10 border-t border-white/10 pt-6">
        <div className="flex items-center gap-3">
          {authorAvatar ? (
            <img src={authorAvatar} className="h-10 w-10 rounded-full object-cover" />
          ) : (
            <div className="h-10 w-10 rounded-full bg-white/10" />
          )}
          <div>
            <div className="font-semibold">{post.user?.displayName}</div>
            <div className="text-white/70 text-sm">{post.user?.handle}</div>
          </div>
          <a href={post.user?.profile_public_id ? `/people/i/${post.user.profile_public_id}` : '#'} className="ml-auto text-sm underline text-white/85">View profile</a>
        </div>
        {/* Placeholder for related posts: link to home or profile for now */}
        <div className="mt-4 text-sm text-white/80">More from this author on their profile.</div>
      </section>
    </main>
  )
}
